import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Copy, KeyRound, MonitorSmartphone, ShieldCheck, ShieldOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errText, fetcher, tsFmt } from "@/lib/api";
import { DigestPanel, TelegramGroupPanel, TelegramPanel, WeeklyPanel } from "@/components/security/TelegramPanel";

function RecoveryCodes({ codes, onDone }) {
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Recovery codes copied"); };
  return (
    <div className="bg-[rgba(252,213,53,0.08)] border border-[#fcd535]/30 rounded p-3 mt-3" data-testid="recovery-codes-panel">
      <div className="text-[11px] font-bold text-[#fcd535] tracking-widest">RECOVERY CODES · shown once</div>
      <p className="text-[11px] text-[#848e9c] mt-1">Each code works once if you lose your authenticator. Store them somewhere safe — they will not be shown again.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2">{codes.map((c) => <div key={c} className="mono text-sm text-white bg-[#0b0e11] rounded px-2 py-1 text-center" data-testid="recovery-code">{c}</div>)}</div>
      <div className="flex gap-2 mt-3"><button onClick={copy} className="btn-ghost rounded px-3 h-8 text-xs flex items-center gap-1" data-testid="recovery-codes-copy"><Copy size={12} /> Copy</button><button onClick={onDone} className="btn-yellow rounded px-3 h-8 text-xs" data-testid="recovery-codes-done">I've saved them</button></div>
    </div>
  );
}

function TwoFactorPanel({ user, refresh }) {
  const { data: st, mutate } = useSWR("/auth/2fa/status", fetcher);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [disable, setDisable] = useState({ password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const required = user?.policy?.require_2fa && user.role !== "admin";
  const start = async () => { setBusy(true); try { setSetup((await api.post("/auth/2fa/setup")).data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const enable = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enable", { code }); setCodes(data.recovery_codes); setSetup(null); setCode(""); toast.success("Two-factor authentication enabled"); mutate(); refresh(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const doDisable = async () => {
    setBusy(true);
    try { await api.post("/auth/2fa/disable", disable); setDisable({ password: "", code: "" }); toast.success("Two-factor authentication disabled"); mutate(); refresh(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const regen = async () => {
    const c = window.prompt("Enter your current authenticator code to regenerate recovery codes");
    if (!c) return;
    try { const { data } = await api.post("/auth/2fa/recovery-codes", { code: c }); setCodes(data.recovery_codes); mutate(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="panel p-4" data-testid="twofa-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">{st?.enabled ? <ShieldCheck size={16} className="text-[#0ecb81]" /> : <ShieldOff size={16} className="text-[#848e9c]" />}<div className="panel-title">Two-factor authentication · Google Authenticator</div></div>
        <span className={`pill ${st?.enabled ? "bg-[rgba(14,203,129,0.12)] text-[#0ecb81]" : "bg-[#2b3139] text-[#848e9c]"}`} data-testid="twofa-status-pill">{st?.enabled ? "ENABLED" : "OFF"}</span>
      </div>
      {required && !st?.enabled && <div className="mt-2 text-xs text-[#f6465d] bg-[rgba(246,70,93,0.1)] rounded px-3 py-2" data-testid="twofa-required-banner">Platform policy requires 2FA. Enrol below to unlock the terminal.</div>}
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      {!st?.enabled && !setup && !codes && (
        <div className="mt-3">
          <p className="text-xs text-[#848e9c]">Adds a 6-digit code from Google Authenticator (or any TOTP app) to every sign-in. You'll also receive 8 one-time recovery codes.</p>
          <button onClick={start} disabled={busy} className="btn-yellow rounded px-4 h-9 text-xs mt-3" data-testid="twofa-start-button">{busy ? "Preparing…" : "SET UP 2FA"}</button>
        </div>
      )}
      {setup && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4" data-testid="twofa-setup">
          <img src={setup.qr_data_url} alt="Scan with Google Authenticator" className="w-44 h-44 rounded bg-white p-1" data-testid="twofa-qr" />
          <div>
            <div className="text-xs text-[#eaecef]">1. Open Google Authenticator → <span className="text-white">+</span> → Scan a QR code.</div>
            <div className="text-xs text-[#848e9c] mt-1">Can't scan? Enter this key manually:</div>
            <div className="mono text-sm text-[#fcd535] bg-[#0b0e11] rounded px-2 py-1 mt-1 break-all select-all" data-testid="twofa-secret">{setup.secret}</div>
            <div className="text-xs text-[#eaecef] mt-3">2. Enter the 6-digit code shown in the app:</div>
            <div className="flex gap-2 mt-1">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="input mono w-36 text-center tracking-[0.3em]" placeholder="000000" inputMode="numeric" data-testid="twofa-enable-code-input" />
              <button onClick={enable} disabled={busy || code.length !== 6} className="btn-yellow rounded px-4 h-9 text-xs" data-testid="twofa-enable-button">{busy ? "Verifying…" : "ACTIVATE"}</button>
              <button onClick={() => setSetup(null)} className="btn-ghost rounded px-3 h-9 text-xs" data-testid="twofa-cancel-setup">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {st?.enabled && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-[#848e9c] mono">enabled {tsFmt(st.enabled_at)} · <span className={st.recovery_codes_remaining <= 2 ? "text-[#f6465d]" : "text-white"} data-testid="recovery-remaining">{st.recovery_codes_remaining} recovery codes left</span> <button onClick={regen} className="underline ml-2 hover:text-white" data-testid="recovery-regenerate">regenerate</button></div>
          {!required && (
            <details className="text-xs" data-testid="twofa-disable-section">
              <summary className="cursor-pointer text-[#848e9c] hover:text-white">Disable two-factor authentication</summary>
              <div className="flex flex-wrap gap-2 mt-2">
                <input type="password" placeholder="Current password" value={disable.password} onChange={(e) => setDisable({ ...disable, password: e.target.value })} className="input w-48" data-testid="twofa-disable-password" />
                <input placeholder="Authenticator code" value={disable.code} onChange={(e) => setDisable({ ...disable, code: e.target.value })} className="input mono w-40" data-testid="twofa-disable-code" />
                <button onClick={doDisable} disabled={busy || !disable.password || disable.code.length < 6} className="btn-red rounded px-3 h-9 text-xs" data-testid="twofa-disable-button">DISABLE</button>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function PasswordPanel() {
  const { changePassword } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.next !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try { await changePassword(f.current, f.next); toast.success("Password changed — other sessions signed out"); setF({ current: "", next: "", confirm: "" }); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-4" data-testid="password-panel">
      <div className="flex items-center gap-2"><KeyRound size={16} className="text-[#fcd535]" /><div className="panel-title">Change password</div></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
        <input type="password" placeholder="Current password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} className="input" autoComplete="current-password" data-testid="password-current-input" />
        <input type="password" placeholder="New password (min 10 chars)" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} className="input" autoComplete="new-password" data-testid="password-new-input" />
        <input type="password" placeholder="Confirm new password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className="input" autoComplete="new-password" data-testid="password-confirm-input" />
      </div>
      <button onClick={save} disabled={busy || !f.current || f.next.length < 10} className="btn-yellow rounded px-4 h-9 text-xs mt-3" data-testid="password-save-button">{busy ? "Saving…" : "UPDATE PASSWORD"}</button>
    </div>
  );
}

function SessionsPanel() {
  const { data, mutate } = useSWR("/auth/sessions", fetcher, { refreshInterval: 30000 });
  const revoke = async (id) => { try { await api.delete(`/auth/sessions/${id}`); toast.success("Session revoked"); mutate(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="panel p-4" data-testid="sessions-panel">
      <div className="flex items-center gap-2"><MonitorSmartphone size={16} className="text-[#3b82f6]" /><div className="panel-title">Active sessions</div></div>
      <div className="mt-2 divide-y divide-[#1e2329]">
        {(data || []).map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 py-2 text-xs" data-testid={`session-row-${s.id}`}>
            <div className="min-w-0"><div className="text-white truncate">{s.user_agent || "unknown device"}{s.current && <span className="pill bg-[rgba(14,203,129,0.12)] text-[#0ecb81] ml-2">THIS DEVICE</span>}</div><div className="mono text-[10px] text-[#848e9c]">ip {s.ip || "—"} · signed in {tsFmt(s.created_at)} · last seen {tsFmt(s.last_seen_at)}</div></div>
            {!s.current && <button onClick={() => revoke(s.id)} className="btn-ghost rounded px-2 h-7 text-[10px] text-[#f6465d] shrink-0" data-testid={`session-revoke-${s.id}`}>Revoke</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Security() {
  const { user, refresh } = useAuth();
  return (
    <div className="space-y-3 fade-in max-w-4xl" data-testid="security-page">
      <div><h1 className="font-display text-xl font-bold text-white">Security</h1><p className="text-xs text-[#848e9c] mt-0.5 mono">{user?.email} · role {user?.role?.toUpperCase()}{user?.last_login_at ? ` · last login ${tsFmt(user.last_login_at)}` : ""}</p></div>
      <TwoFactorPanel user={user} refresh={refresh} />
      <TelegramPanel />
      <TelegramGroupPanel />
      <DigestPanel />
      <WeeklyPanel />
      <PasswordPanel />
      <SessionsPanel />
    </div>
  );
}
